import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Box, Dialog, DialogContent, Typography, Chip } from "@mui/material";
import Devider from "@mui/material/Divider";
import CancelIcon from "@mui/icons-material/Cancel";
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { useLocationCertificates } from "../../Hooks/useLocationCertificates";
import { useProductImages } from "../../Hooks/useProductImages";
import { useProductCertificates } from "../../Hooks/useProductCertificates";
import { useProductDetails } from "../../Hooks/useProductDetails";
import { useEventImages } from "../../Hooks/useEventImages";
import { TraceEventEntityInstances } from "../../graphql/queries"

import { GroupedEventsModalTypes, Certificate } from "../../types";
import CertificatePreview from "../Common/CertificatePreview";

import ProductSlider from "../Common/ProductSlider";
import FormDetails from "../Common/FormsDetailsModal";
import LinkButton from "../UI/LinkButton";
import StyledSelect from "../UI/StyledSelect";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { NOT_AVAILABLE } from "../../constants";


const EventDetails = ({ open, onClose, eventData, productDetails: initialProductDetails, orgId }: GroupedEventsModalTypes) => {
    const { fetchLocationCertificates, getCertificatesForEvent } = useLocationCertificates();
    const { productImages, fetchProductImages, loading: productImagesLoading } = useProductImages();
    const { productCertificates, fetchProductCertificates } = useProductCertificates();
    const { productDetails: fetchedProductDetails, fetchProductDetails, isProductLoading } = useProductDetails();
    const { eventImages, fetchEventImages, isEventLoading } = useEventImages();
    const [certificateModalOpen, setCertificateModalOpen] = useState(false);
    const [productCertificateModalOpen, setProductCertificateModalOpen] = useState(false);
    const [showFormDetails, setShowFormDetails] = useState(false);

    // Merge initial product details (from parent) with fetched product details
    const productDetails = useMemo(() => {
        return { ...initialProductDetails, ...fetchedProductDetails };
    }, [initialProductDetails, fetchedProductDetails]);


    // State to store the specific product certificates for the modal
    const [selectedProductCertificates, setSelectedProductCertificates] = useState<Certificate[]>([]);

    // Determine if we have multiple events (array) or single event
    const isMultipleEvents = Array.isArray(eventData);
    const eventsArray = useMemo(() => {
        return isMultipleEvents ? eventData : (eventData ? [eventData] : []);
    }, [isMultipleEvents, eventData]);

    // For multiple events: location and event selection
    const [selectedLocation, setSelectedLocation] = useState<string>("ALL_LOCATIONS");
    const [selectedEventId, setSelectedEventId] = useState<string>("");

    // Get unique locations from events
    const uniqueLocations = useMemo(() => {
        const locations = eventsArray.map(e => e.location).filter(Boolean);
        return [...new Set(locations)];
    }, [eventsArray]);

    // Get location options with "All locations" as first option
    const locationOptions = useMemo(() => {
        if (!isMultipleEvents || uniqueLocations.length === 0) return [];
        return [
            { value: "ALL_LOCATIONS", label: "All locations" },
            ...uniqueLocations.map(location => ({ value: location, label: location }))
        ];
    }, [isMultipleEvents, uniqueLocations]);

    // Get events filtered by selected location
    const eventsForLocation = useMemo(() => {
        if (!isMultipleEvents) return [];
        if (selectedLocation === "ALL_LOCATIONS") {
            return eventsArray;
        }
        return eventsArray.filter(e => e.location === selectedLocation);
    }, [eventsArray, selectedLocation, isMultipleEvents]);

    // Get the currently selected event object
    const currentEvent = useMemo(() => {
        if (!isMultipleEvents) {
            return eventData;
        }
        return eventsArray.find(e => e.eventID === selectedEventId) || null;
    }, [isMultipleEvents, eventData, eventsArray, selectedEventId]);

    // Fetch product details for all events when modal opens
    useEffect(() => {
        if (open && eventsArray.length > 0) {
            // Build event product instances for all events that don't have product details yet
            const missingEvents = eventsArray.filter(event => {
                const eventId = event.eventID;
                return eventId && !productDetails?.[eventId];
            });

            if (missingEvents.length > 0) {
                const eventProdInstances = missingEvents.map(event => ({
                    eventId: event.eventID,
                    productInstanceIds: event.outputProductInstances || []
                }));
                fetchProductDetails(eventProdInstances);
            }
        }
    }, [open, eventsArray, productDetails, fetchProductDetails]);

    // Initialize location and event selection when modal opens or eventData changes
    useEffect(() => {
        if (open && isMultipleEvents && uniqueLocations.length > 0) {
            // If only one unique location, select that location, otherwise default to "All locations"
            if (uniqueLocations.length === 1) {
                setSelectedLocation(uniqueLocations[0]);
            } else {
                setSelectedLocation("ALL_LOCATIONS");
            }
        }
    }, [open, isMultipleEvents, uniqueLocations]);

    // When location changes, select first event for that location
    useEffect(() => {
        if (isMultipleEvents && eventsForLocation.length > 0) {
            const firstEvent = eventsForLocation[0];
            if (firstEvent && (!selectedEventId || !eventsForLocation.find(e => e.eventID === selectedEventId))) {
                setSelectedEventId(firstEvent.eventID);
            }
        }
    }, [isMultipleEvents, selectedLocation, eventsForLocation, selectedEventId]);

    // Reset selections when modal closes
    useEffect(() => {
        if (!open) {
            setSelectedLocation("ALL_LOCATIONS");
            setSelectedEventId("");
        }
    }, [open]);

    const certificates = currentEvent?.eventID ? getCertificatesForEvent(currentEvent.eventID) : [];

    const currentProductCertificates = currentEvent?.eventID
        ? (productCertificates[currentEvent.eventID] ?? undefined)
        : undefined;

    useEffect(() => {
        if (currentEvent?.eventID) {
            fetchLocationCertificates([currentEvent.eventID]);
            fetchEventImages([currentEvent.eventID]);
        }
    }, [currentEvent?.eventID, fetchLocationCertificates, fetchEventImages]);

    // Fetch product images when modal opens or selected event changes
    useEffect(() => {
        if (open && currentEvent?.eventID && productDetails?.[currentEvent.eventID]) {
            const products = productDetails[currentEvent.eventID] || [];
            if (products.length > 0) {
                const eventProdInstances = [{
                    eventId: currentEvent.eventID,
                    productInstanceIds: products.map(p => p.productInstanceId).filter(Boolean)
                }];
                fetchProductImages(eventProdInstances);
                fetchProductCertificates(eventProdInstances);
            }
        }
    }, [open, currentEvent?.eventID, productDetails, fetchProductImages, fetchProductCertificates]);

    // Get products for the current event
    const products = currentEvent?.eventID ? (productDetails?.[currentEvent.eventID] || []) : [];

    const { data: EntityData } = useQuery(TraceEventEntityInstances, {
        variables: {
            input: {
                orgId,
                eventId: currentEvent?.eventID || "",
            }
        },
        skip: !orgId,
        fetchPolicy: "network-only",
    });

    const groupedEntities = useMemo(() => {
        const instances: { entityType: string; entityName: string }[] =
            EntityData?.traceEventEntityInstances?.instances ?? [];
        return instances.reduce<Map<string, string[]>>((map, inst) => {
            if (!map.has(inst.entityType)) map.set(inst.entityType, []);
            map.get(inst.entityType)!.push(inst.entityName);
            return map;
        }, new Map());
    }, [EntityData]);



    return (
        <div className="eventDetails">
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" >
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ backgroundColor: "#f5f5f5", padding: 2, }}>
                    <Typography px={1} sx={{ fontWeight: "bold", fontSize: "15px" }}>
                        Event : {currentEvent?.eventDescription}
                    </Typography>
                    <CancelIcon fontSize="medium" onClick={onClose} style={{ cursor: "pointer", }} />
                </Box>
                <DialogContent sx={{ maxHeight: { xs: 'calc(var(--app-vh, 1vh) * 80)', md: 'calc(var(--app-vh, 1vh) * 75)' }, overflowY: 'auto', overflowX: 'auto', padding: 2 }}>

                    {/* Location: Always show StyledSelect, disabled if single location */}
                    {(isMultipleEvents || currentEvent?.location !== NOT_AVAILABLE) && (
                        <StyledSelect
                            label={isMultipleEvents ? "Select Location" : "Location"}
                            value={isMultipleEvents ? selectedLocation : (currentEvent?.location || "")}
                            onChange={setSelectedLocation}
                            options={isMultipleEvents ? locationOptions : [{ value: currentEvent?.location || "", label: currentEvent?.location || "" }]}
                            disabled={!isMultipleEvents || uniqueLocations.length <= 1}
                        />
                    )}

                    {/* Event: Always show StyledSelect when there are events, disabled if single event */}
                    {(isMultipleEvents || currentEvent) && (
                        <StyledSelect
                            label="Event Timestamp"
                            isArrowEnabled={true}
                            value={selectedEventId || currentEvent?.eventID || ""}
                            onChange={setSelectedEventId}
                            options={isMultipleEvents ? eventsForLocation.map(event => ({
                                value: event.eventID,
                                label: `Event at : ${event.eventTime}`
                            })) : currentEvent ? [{
                                value: currentEvent.eventID,
                                label: `Event at : ${currentEvent.eventTime}`
                            }] : []}
                            disabled={!isMultipleEvents}
                        />
                    )}

                    {/* Location Certificates */}
                    {certificates?.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", mt: -1 }}>
                            {currentEvent?.location !== NOT_AVAILABLE && <LocationOnOutlinedIcon sx={{ fontSize: "14px !important", mr: 0.2 }} />}
                            {currentEvent?.location !== NOT_AVAILABLE && <Typography variant="body2">{currentEvent?.location} : </Typography>}
                            <LinkButton
                                icon={<FileCopyOutlinedIcon />}
                                tooltip="View Location Certificates"
                                onClick={() => setCertificateModalOpen(true)}
                            >
                                Certificates
                            </LinkButton>
                        </Box>
                    )}

                    {/* Entity Instances */}
                    {groupedEntities.size > 0 && (
                        <Box sx={{ mt: 1.5, mb: 0.5 }}>
                            <Devider sx={{ width: "100%", mb: 2 }} />

                            {Array.from(groupedEntities.entries()).map(([entityType, names]) => (
                                <Box key={entityType} sx={{
                                    display: "flex",
                                    alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.5
                                }}>
                                    <Typography sx={{
                                        fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap",
                                        color: "text.secondary",
                                    }}>
                                        {entityType} :
                                    </Typography>
                                    {names.map(name => (
                                        <Chip key={name} label={name} size="small"
                                            sx={{ fontSize: "11px", height: "20px" }} />
                                    ))}
                                </Box>
                            ))}
                            <Devider sx={{ width: "100%", mt: 2 }} />

                        </Box>
                    )}

                    {/* Product Slider */}
                    <Box sx={{ mt: 2, mb: 1, display: "flex", justifyContent: "center" }}>
                        <ProductSlider
                            products={products}
                            loading={productImagesLoading || (currentEvent?.eventID ? (isProductLoading(currentEvent.eventID) || isEventLoading(currentEvent.eventID)) : false)}
                            productImages={productImages}
                            eventImage={eventImages?.[currentEvent?.eventID || '']}
                            productCertificates={currentProductCertificates ?? undefined}
                            onProductCertificatesClick={(certificates) => {
                                if (certificates && certificates.length > 0) {
                                    setSelectedProductCertificates(certificates);
                                    setProductCertificateModalOpen(true);
                                }
                            }}
                        />
                    </Box>
                    {/* Form Details */}
                    {currentEvent?.formDetails && (
                        <Box sx={{ display: "flex", justifyContent: "center", }}>
                            <LinkButton
                                icon={<DescriptionOutlinedIcon />}
                                tooltip="View Additional Details"
                                onClick={() => setShowFormDetails(true)}
                            >
                                Additional Details
                            </LinkButton>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <CertificatePreview
                open={certificateModalOpen}
                onClose={() => { setCertificateModalOpen(false) }}
                certificates={certificates}
            />

            <CertificatePreview
                open={productCertificateModalOpen}
                onClose={() => {
                    setProductCertificateModalOpen(false);
                    setSelectedProductCertificates([]);
                }}
                certificates={selectedProductCertificates}
            />
            {showFormDetails && currentEvent &&
                <FormDetails event={currentEvent} open={showFormDetails} setOpen={setShowFormDetails} />
            }
        </div>
    )
}

export default EventDetails
